/* Themes.
 *
 * A theme swaps token *values*; it never swaps token *roles*. The accent means
 * "this is yours" in every theme, green means finished, red means wrong. That
 * is what keeps colour informative rather than decorative, and it is why this
 * module is thirty lines rather than a theming framework: all the work is in
 * `styles.css`, where one `[data-theme]` block redefines the palette and no
 * other rule has to know a theme exists.
 *
 * Three details that are easy to get wrong and expensive to leave wrong:
 *
 *   · **Applied before the first paint.** A theme read after React mounts
 *     means every load flashes the default palette and then repaints in the
 *     chosen one. We have already fixed one flash-of-wrong-layout bug in this
 *     app; a flash of wrong colour is the same defect wearing a different hat.
 *     `main.tsx` calls `applyTheme` at module scope, before `createRoot`.
 *
 *   · **Storage can throw.** Safari in private mode, and any browser set to
 *     block site data, throws from `localStorage` rather than returning null.
 *     Reading it unguarded during render took the whole app to a blank page
 *     once already, so this goes through the guarded helpers in `api.ts`.
 *
 *   · **The browser chrome is part of the theme.** On a phone the address bar
 *     and the task-switcher card take their colour from `theme-color`. Leaving
 *     it at the default paints a warm charcoal bar above a black-and-violet
 *     app, which reads as a rendering bug rather than as a theme.
 */

import { useCallback, useEffect, useState } from 'react';
import { readLocal, writeLocal } from '../api';

export type ThemeName = 'ember' | 'violet' | 'paper';

export const THEMES: { name: ThemeName; label: string; note: string }[] = [
  { name: 'ember', label: 'Ember', note: 'Warm charcoal and orange' },
  { name: 'violet', label: 'Violet', note: 'Black and purple' },
  /* The light one. Google Calendar and Notion are both light by default, and a
     planner read on a laptop at nine in the morning is read in daylight. */
  { name: 'paper', label: 'Paper', note: 'Light, like a page' }
];

/** The page background of each theme, for the mobile browser chrome. */
const CHROME: Record<ThemeName, string> = { ember: '#101317', violet: '#0A0812', paper: '#FBFAF8' };

const KEY = 'amon.theme';
const DEFAULT: ThemeName = 'ember';

function isTheme(v: string | null): v is ThemeName {
  return v === 'ember' || v === 'violet' || v === 'paper';
}

export function readTheme(): ThemeName {
  const stored = readLocal(KEY);
  return isTheme(stored) ? stored : DEFAULT;
}

/**
 * Put a theme on the document. Safe to call before React exists, which is the
 * point — see the note about painting the default first.
 */
export function applyTheme(theme: ThemeName): void {
  const root = document.documentElement;
  /* The default theme is plain `:root`, so it carries no attribute at all.
     That keeps the default cheap and means a stored value we no longer
     recognise degrades to the default rather than to an unstyled page. */
  if (theme === DEFAULT) root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', theme);

  let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = 'theme-color';
    document.head.appendChild(meta);
  }
  meta.content = CHROME[theme];
}

const THEME_EVENT = 'amon:theme';

/**
 * The theme the account chose, arriving from the server — on sign-in, or
 * pushed from another device that just changed it.
 *
 * Only a real choice is followed. An account that never chose one has
 * `theme: null`, and null is not the default: it must leave the device it is
 * signing in on exactly as that device had it.
 */
export function followAccountTheme(theme: string | null | undefined): void {
  if (!isTheme(theme ?? null)) return;
  writeLocal(KEY, theme as ThemeName);
  applyTheme(theme as ThemeName);
  window.dispatchEvent(new Event(THEME_EVENT));
}

/** Read and change the theme. Persists, and applies immediately. */
export function useTheme(): [ThemeName, (next: ThemeName) => void] {
  const [theme, setTheme] = useState<ThemeName>(readTheme);

  /* Not only on change: a second tab, or a `main.tsx` that ran before this
     component mounted, can leave the document and this state disagreeing. */
  useEffect(() => { applyTheme(theme); }, [theme]);

  /* And when the account's choice arrives from elsewhere, the control follows
     rather than showing the theme the page no longer has. */
  useEffect(() => {
    const follow = () => setTheme(readTheme());
    window.addEventListener(THEME_EVENT, follow);
    return () => window.removeEventListener(THEME_EVENT, follow);
  }, []);

  const change = useCallback((next: ThemeName) => {
    writeLocal(KEY, next);
    setTheme(next);
  }, []);

  return [theme, change];
}
